import { Directive, effect, ElementRef, inject, input } from '@angular/core'
import DOMPurify from 'dompurify'

const normalizeInjectedHtml = (content: string): string => {
  return content.replace(/<app-image(\s[^>]*)\/>/g, '<app-image$1></app-image>')
}

// Hosts allowed to serve embedded video iframes in CMS content
const embedHosts = new Set(['pregnantwithdiabetes.com', 'www.pregnantwithdiabetes.com'])

const isAllowedEmbedSrc = (src: string): boolean => {
  try {
    const url = new URL(src)
    return url.protocol === 'https:' && embedHosts.has(url.hostname)
  } catch {
    return false
  }
}

// CMS content is untrusted until sanitized: strip anything DOMPurify considers
// dangerous and drop video iframes whose src is not a trusted embed host.
DOMPurify.addHook('uponSanitizeElement', (node, { tagName }) => {
  if (tagName !== 'iframe') {
    return
  }
  const element = node as Element
  if (!isAllowedEmbedSrc(element.getAttribute('src') ?? '')) {
    element.remove()
  }
})

const sanitizeInjectedHtml = (content: string): string => {
  return DOMPurify.sanitize(content, {
    ADD_TAGS: ['app-image', 'iframe'],
    ADD_ATTR: ['target', 'allowfullscreen'],
  })
}

@Directive({
  selector: '[appInjectHTML]',
})
export class InjectHTMLDirective {
  readonly appInjectHTML = input.required<string>()

  private host = inject(ElementRef)

  constructor() {
    effect(() => {
      this.host.nativeElement.innerHTML = sanitizeInjectedHtml(normalizeInjectedHtml(this.appInjectHTML()))
    })
  }
}
